import { useCallback, useEffect, useMemo, useState } from 'react'
import { onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, getDoc, onSnapshot } from 'firebase/firestore'
import {
  demoModeAvailable,
  firebaseAuth,
  firestoreDb,
  isFirebaseConfigured,
} from '../lib/firebase'
import { AuthContext } from './auth-context'

const demoUser = {
  email: 'demo@presspass.local',
  displayName: 'Demo Administrator',
  photoURL: '',
  role: 'admin',
}

function validAccess(firebaseUser, claims, access) {
  return claims.portalAccess === true
    && claims.appId === 'presspass'
    && claims.ssoVersion === 2
    && claims.centralUid === access?.centralUid
    && Number(claims.grantVersion) === access?.grantVersion
    && access?.appId === 'presspass'
    && access?.enabled === true
    && ['admin', 'editor'].includes(access?.role)
    && firebaseUser.emailVerified
}

function portalUser(firebaseUser, access) {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email.toLowerCase(),
    displayName: firebaseUser.displayName || firebaseUser.email,
    photoURL: firebaseUser.photoURL || '',
    role: access.role,
  }
}

function accessFromClaims(claims) {
  return {
    appId: claims.appId,
    centralUid: claims.centralUid,
    grantVersion: Number(claims.grantVersion),
    role: claims.role,
    enabled: true,
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(Boolean(firebaseAuth))
  const [error, setError] = useState('')
  const [unauthorizedEmail, setUnauthorizedEmail] = useState('')
  const [demoMode, setDemoMode] = useState(false)

  useEffect(() => {
    if (!firebaseAuth || !firestoreDb) return undefined
    let unsubscribeAccess = () => {}
    const unsubscribeAuth = onAuthStateChanged(firebaseAuth, async (firebaseUser) => {
      unsubscribeAccess()
      setLoading(true)
      if (!firebaseUser?.email) {
        setUser(null)
        setLoading(false)
        return
      }

      try {
        const token = await firebaseUser.getIdTokenResult(true)
        const accessRef = doc(firestoreDb, 'ssoAccess', firebaseUser.uid)
        let projectionReadable = true
        let access
        try {
          const snapshot = await getDoc(accessRef)
          access = snapshot.exists() ? snapshot.data() : null
        } catch (reason) {
          if (reason?.code !== 'permission-denied') throw reason
          projectionReadable = false
          access = accessFromClaims(token.claims)
        }
        if (!validAccess(firebaseUser, token.claims, access)) {
          throw new Error('Open PreNexus from the LPHTM portal. This session is not authorized.')
        }
        setError('')
        setUnauthorizedEmail('')
        setUser(portalUser(firebaseUser, access))
        if (projectionReadable) unsubscribeAccess = onSnapshot(accessRef, async (nextSnapshot) => {
          const nextAccess = nextSnapshot.exists() ? nextSnapshot.data() : null
          if (!validAccess(firebaseUser, token.claims, nextAccess)) {
            await signOut(firebaseAuth)
            setUser(null)
            setError('Your PreNexus access changed. Open the workspace again from the LPHTM portal.')
            return
          }
          setUser(portalUser(firebaseUser, nextAccess))
        }, async () => {
          await signOut(firebaseAuth)
          setUser(null)
          setError('PreNexus could not verify the current portal grant.')
        })
      } catch (reason) {
        setUnauthorizedEmail(firebaseUser.email)
        setUser(null)
        await signOut(firebaseAuth)
        setError(reason instanceof Error ? reason.message : 'Tidak dapat memverifikasi hak akses.')
      } finally {
        setLoading(false)
      }
    })
    return () => { unsubscribeAccess(); unsubscribeAuth() }
  }, [])

  const enterDemo = useCallback(() => {
    if (!demoModeAvailable) return
    setDemoMode(true)
    setUnauthorizedEmail('')
    setError('')
    setUser(demoUser)
  }, [])

  const logout = useCallback(async () => {
    if (demoMode) {
      setDemoMode(false)
      setUser(null)
      return
    }
    if (firebaseAuth) await signOut(firebaseAuth)
  }, [demoMode])

  const value = useMemo(() => ({
    user,
    loading,
    error,
    unauthorizedEmail,
    demoMode,
    demoModeAvailable,
    isFirebaseConfigured,
    enterDemo,
    logout,
  }), [demoMode, enterDemo, error, loading, logout, unauthorizedEmail, user])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
